import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

export interface TruckItem {
  id: string;
  instance: number;
  description: string;
  code: string;
  areaName: string;
  phaseName: string;
  phaseId: string | null;
  width: number;
  height: number;
  depth: number;
  weight: number;
  type: 'base' | 'upper' | 'tall';
  x: number;
  y: number;
  z: number;
  stacked: boolean;
}

export interface TruckRow {
  zStart: number;
  depth: number;
  itemCount: number;
}

export interface TruckResult {
  items: TruckItem[];
  weight: number;
  rows: TruckRow[];
  fillPct: number;
  lengthUsed: number;
  phase: string | null;
}

export interface TruckSpec {
  name: string;
  length: number;
  width: number;
  height: number;
  maxWeight: number;
}

export interface PackResponse {
  trucks: TruckResult[];
  skippedItems: number;
  truckSpec: TruckSpec;
  totalItems: number;
}

export interface TruckLoadConfigRow {
  id: string;
  project_id: string;
  name: string;
  config: string;
  trucks_data: string;
  cost_per_truck: number;
  import_cost: number;
  tariff_cost: number;
  created_at: string;
}

export function usePackTrucks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId, truckType, maxStack, padding, priority,
    }: {
      projectId: string;
      truckType: string;
      maxStack: number;
      padding: number;
      priority: string;
    }) => {
      const { data } = await apiClient.post<PackResponse>(
        `/truck-loader/project/${projectId}/pack`,
        { truckType, maxStack, padding, priority },
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['truck-loader', vars.projectId] });
    },
  });
}

export function useTruckResults(projectId: string | undefined) {
  return useQuery({
    queryKey: ['truck-loader', projectId, 'results'],
    queryFn: async () => {
      const { data } = await apiClient.get<TruckLoadConfigRow | null>(
        `/truck-loader/project/${projectId}/results`,
      );
      return data;
    },
    enabled: !!projectId,
  });
}

export function useTruckConfigs(projectId: string | undefined) {
  return useQuery({
    queryKey: ['truck-loader', projectId, 'configs'],
    queryFn: async () => {
      const { data } = await apiClient.get<TruckLoadConfigRow[]>(
        `/truck-loader/project/${projectId}/configs`,
      );
      return data;
    },
    enabled: !!projectId,
  });
}

export function useSaveTruckConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId, name, config, trucksData, costPerTruck, importCost, tariffCost,
    }: {
      projectId: string;
      name: string;
      config: string;
      trucksData: string;
      costPerTruck?: number;
      importCost?: number;
      tariffCost?: number;
    }) => {
      const { data } = await apiClient.post<TruckLoadConfigRow>(
        `/truck-loader/project/${projectId}/save`,
        { name, config, trucksData, costPerTruck, importCost, tariffCost },
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['truck-loader', vars.projectId, 'configs'] });
    },
  });
}

export function useDeleteTruckConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ configId, projectId }: { configId: string; projectId: string }) => {
      await apiClient.delete(`/truck-loader/config/${configId}`);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['truck-loader', vars.projectId, 'configs'] });
    },
  });
}
